import React, { useMemo } from "react";
import { FormControlLabel, Checkbox } from "@mui/material";
import { useUserTheme } from "src/shared/utils/useUserTheme";
import { ToastContainer } from "react-toastify";
import { Category } from "src/shared/services/interfacev2";
import TableToggleView from "./TableToggleView";
import CustomDropdownSelect from "src/business-types/grocery/components/DropdownSelect";
import CommonButtons from "src/shared/components/CommonButtons";

interface ProductToolbarProps {
  categories: Category[];
  categoryFilter: string;
  onCategoryChange: (value: string) => void;
  showDeletedProducts: boolean;
  onToggleDeleted: () => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onAddProduct: () => void;
  viewMode: "table" | "icon";
  onViewModeChange: (mode: "table" | "icon") => void;
}

const ProductToolbar: React.FC<ProductToolbarProps> = ({
  categories,
  categoryFilter,
  onCategoryChange,
  showDeletedProducts,
  onToggleDeleted,
  searchTerm,
  onSearchChange,
  onAddProduct,
  viewMode,
  onViewModeChange,
}) => {
  const { fontClass, borderHighlight } = useUserTheme();

  const categoryOptions = useMemo(
    () =>
      categories.map((cat) => ({
        _id: cat._id.$oid,
        CategoryName: cat.name,
        otherName: "",
      })),
    [categories],
  );

  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-2 sm:p-2 shadow-sm relative overflow-visible">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 items-end w-full overflow-visible">
        {/* 1. View Toggle */}
        <div className="w-full min-w-0">
          <TableToggleView viewMode={viewMode} onViewModeChange={onViewModeChange} />
        </div>

        <div className="w-full min-w-0 relative">
          <CustomDropdownSelect
            name="categoryFilter"
            title="Category"
            titleClassName="font-bold"
            value={categoryFilter}
            options={categoryOptions}
            onChange={onCategoryChange}
            titleofSelect="All Categories"
            mandatory={true}
            className="!mb-0 w-full"
          />
        </div>

        {/* 3. Search Bar */}
        <div className="w-full min-w-0">
          <input
            id="product-search"
            type="text"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search by product name..."
            className={`h-[38px] w-full rounded-md border bg-white px-3 text-sm text-black placeholder-gray-400 focus:outline-none focus:ring-2 ${borderHighlight} ${fontClass}`}
          />
        </div>

        {/* 4. View Deleted Checkbox */}
        <div className="w-full min-w-0 flex h-[38px] items-center justify-center rounded-md border border-gray-200 bg-gray-50/50 px-2">
          <FormControlLabel
            control={<Checkbox checked={showDeletedProducts} onChange={onToggleDeleted} size="small" sx={{ padding: "3px", "&.Mui-checked": { color: "#2563eb" } }} />}
            label={<span className="whitespace-nowrap text-xs font-medium text-gray-700 select-none">View deleted</span>}
            className="!m-0 flex items-center justify-center w-full"
          />
        </div>

        {/* 5. Add Product Button */}
        <div className="w-full min-w-0">
          <CommonButtons label="Add Product" onClick={onAddProduct} className="!h-[38px] w-full" />
        </div>
      </div>

      {/* Toast */}
      <ToastContainer />
    </div>
  );
};

export default ProductToolbar;
